import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box, Typography, TextField, Select, MenuItem, FormControl, InputLabel,
    Accordion, AccordionSummary, AccordionDetails, Button, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions, Chip, InputAdornment, Autocomplete,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useUser } from '../../context/userContext';
import { getAllFaqs, createFaq, updateFaq, deleteFaq, uploadFaqImage } from '../../api/faqApi';
import Highlight from '../learn/Highlight';
import { findSimilarTitles } from '../learn/findSimilar';

const poppins = "'Poppins', sans-serif";

const EMPTY_FAQ = { faqId: 0, category: '', subCategory: '', title: '', description: '', imageUrls: '', videoLinks: '', sortOrder: 0 };

function YouTubeEmbed({ url }) {
    const id = url.match(/(?:youtu\.be\/|v=|\/embed\/)([a-zA-Z0-9_-]{11})/)?.[1];
    if (!id) return <a href={url} target="_blank" rel="noreferrer" style={{ color: '#1A8CFF', fontSize: '0.8rem' }}>{url}</a>;
    return <iframe width="100%" style={{ maxWidth: 480, aspectRatio: '16/9', borderRadius: 8, border: 'none', marginTop: 8 }} src={`https://www.youtube.com/embed/${id}`} title="YouTube" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />;
}

export default function FaqPage() {
    const { user } = useUser();
    const [faqs, setFaqs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [catFilter, setCatFilter] = useState('All');
    const [expanded, setExpanded] = useState(false);

    // Modal
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FAQ);
    const [deleteId, setDeleteId] = useState(null);

    // Admin / Super Admin can always manage FAQs even without the explicit
    // ManageFaq right granted — matches the Documents admin pattern.
    const canManage = user?.roleRights?.includes('ManageFaq')
        || user?.roles?.some((r) => ['Admin', 'Super Admin'].includes(r));

    const fetchFaqs = async () => {
        try {
            const res = await getAllFaqs();
            setFaqs(res.data);
        } catch (e) { console.error('Failed to load FAQs', e); }
        finally { setLoading(false); }
    };
    useEffect(() => { fetchFaqs(); }, []);

    // Deep-link: /tools/faq?add=1 auto-opens the Add dialog (used by the
    // "Add Tutorial" button on /tutorials).
    const [searchParams, setSearchParams] = useSearchParams();
    useEffect(() => {
        if (!canManage) return;
        if (searchParams.get('add') === '1') {
            setForm(EMPTY_FAQ);
            setOpen(true);
            // Strip the param so a refresh doesn't reopen the dialog.
            const next = new URLSearchParams(searchParams);
            next.delete('add');
            setSearchParams(next, { replace: true });
        }
    }, [searchParams, canManage, setSearchParams]);

    const categories = useMemo(() => {
        const cats = [...new Set(faqs.map(f => f.category).filter(Boolean))].sort();
        return ['All', ...cats];
    }, [faqs]);

    const filtered = useMemo(() => {
        let list = faqs;
        if (catFilter !== 'All') list = list.filter(f => f.category === catFilter);
        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter(f => f.title.toLowerCase().includes(q) || f.description.toLowerCase().includes(q) || f.category.toLowerCase().includes(q));
        }
        return list;
    }, [faqs, catFilter, search]);

    const handleSave = async () => {
        try {
            const payload = {
                ...form,
                imageUrls: form.imageUrls || null,
                videoLinks: form.videoLinks || null,
            };
            if (form.faqId) await updateFaq(payload);
            else await createFaq(payload);
            setOpen(false); setForm(EMPTY_FAQ);
            fetchFaqs();
        } catch (e) { alert('Failed to save FAQ'); console.error(e); }
    };

    const handleDelete = async () => {
        try {
            await deleteFaq(deleteId);
            setDeleteId(null);
            fetchFaqs();
        } catch (e) { alert('Failed to delete FAQ'); }
    };

    const parseList = (json) => {
        if (!json) return [];
        try { const arr = JSON.parse(json); return Array.isArray(arr) ? arr.filter(Boolean) : []; }
        catch { return json.split('\n').map(s => s.trim()).filter(Boolean); }
    };

    if (loading) return <Box sx={{ p: 3, fontFamily: poppins }}>Loading...</Box>;

    return (
        <Box sx={{ fontFamily: poppins, maxWidth: 1000, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <div>
                    <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', fontFamily: poppins }}>FAQ</Typography>
                    <Typography sx={{ fontSize: '0.8rem', color: '#64748B', fontFamily: poppins }}>Frequently Asked Questions</Typography>
                </div>
                {canManage && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setForm(EMPTY_FAQ); setOpen(true); }}
                        sx={{ fontFamily: poppins, fontSize: '0.8rem', fontWeight: 600, textTransform: 'none', borderRadius: '8px', background: 'linear-gradient(135deg,#1A8CFF,#0F62CC)', boxShadow: 'none' }}>
                        Add FAQ
                    </Button>
                )}
            </Box>

            {/* Search & Filter */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                <TextField size="small" placeholder="Search FAQ..." value={search} onChange={(e) => setSearch(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94A3B8', fontSize: 18 }} /></InputAdornment> }}
                    sx={{ flex: 1, minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: poppins, fontSize: '0.85rem' } }} />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <Select value={catFilter} onChange={(e) => setCatFilter(e.target.value)}
                        sx={{ borderRadius: '8px', fontFamily: poppins, fontSize: '0.85rem' }}>
                        {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </Select>
                </FormControl>
            </Box>

            {/* FAQ List */}
            {filtered.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6, color: '#94A3B8' }}>
                    <Typography sx={{ fontFamily: poppins, fontSize: '0.9rem' }}>No FAQs found</Typography>
                </Box>
            ) : filtered.map((faq) => (
                <Accordion key={faq.faqId} expanded={expanded === faq.faqId} onChange={(_, isExpanded) => setExpanded(isExpanded ? faq.faqId : false)}
                    sx={{ mb: 1, borderRadius: '10px !important', '&:before': { display: 'none' }, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}
                        sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.5, my: 1 } }}>
                        <Chip label={faq.category} size="small" sx={{ fontFamily: poppins, fontSize: '0.68rem', fontWeight: 600, bgcolor: '#E0E7FF', color: '#3730A3', borderRadius: 4 }} />
                        <Typography sx={{ fontFamily: poppins, fontSize: '0.88rem', fontWeight: 600, color: '#0F172A', flex: 1 }}><Highlight text={faq.title} query={search} /></Typography>
                        {canManage && (
                            <Box sx={{ display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                                <IconButton size="small" onClick={() => { setForm({ ...faq, imageUrls: faq.imageUrls || '', videoLinks: faq.videoLinks || '' }); setOpen(true); }}>
                                    <EditIcon sx={{ fontSize: 16, color: '#64748B' }} />
                                </IconButton>
                                <IconButton size="small" onClick={() => setDeleteId(faq.faqId)}>
                                    <DeleteIcon sx={{ fontSize: 16, color: '#EF4444' }} />
                                </IconButton>
                            </Box>
                        )}
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
                        <Typography sx={{ fontFamily: poppins, fontSize: '0.82rem', color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                            <Highlight text={faq.description} query={search} />
                        </Typography>
                        {/* Images */}
                        {parseList(faq.imageUrls).length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                                {parseList(faq.imageUrls).map((url, i) => {
                                    const src = url.startsWith('/') ? `${window.location.protocol}//${window.location.hostname}:5100${url}` : url;
                                    return (
                                        <a key={i} href={src} target="_blank" rel="noreferrer">
                                            <img src={src} alt="" style={{ maxWidth: 200, maxHeight: 150, borderRadius: 8, objectFit: 'cover', border: '1px solid #E2E8F0' }} />
                                        </a>
                                    );
                                })}
                            </Box>
                        )}
                        {/* Videos */}
                        {parseList(faq.videoLinks).length > 0 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
                                {parseList(faq.videoLinks).map((url, i) => <YouTubeEmbed key={i} url={url} />)}
                            </Box>
                        )}
                    </AccordionDetails>
                </Accordion>
            ))}

            {/* Add/Edit Dialog */}
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily: poppins, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {form.faqId ? 'Edit FAQ' : 'Add FAQ'}
                    <IconButton size="small" onClick={() => setOpen(false)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                    <Autocomplete
                        freeSolo
                        options={[...new Set(faqs.map(f => f.category).filter(Boolean))]}
                        value={form.category || ''}
                        onInputChange={(_, v) => setForm({ ...form, category: v || '' })}
                        renderInput={(params) => (
                            <TextField {...params} label="Category" size="small"
                                helperText="Pick from existing or type a new category"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: poppins } }}
                            />
                        )}
                    />
                    <Autocomplete
                        freeSolo
                        options={[...new Set(faqs.map(f => f.subCategory).filter(Boolean))]}
                        value={form.subCategory || ''}
                        onInputChange={(_, v) => setForm({ ...form, subCategory: v || '' })}
                        renderInput={(params) => (
                            <TextField {...params} label="Sub-category (optional)" size="small"
                                helperText="Pick from existing or type a new sub-category"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: poppins } }}
                            />
                        )}
                    />
                    <TextField label="Title" size="small" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: poppins } }} />
                    {(() => {
                        const sim = findSimilarTitles(form.title, faqs, { idKey: 'faqId', excludeId: form.faqId });
                        if (!sim.length) return null;
                        return (
                            <Box sx={{ bgcolor: '#FEF3C7', borderLeft: '3px solid #F59E0B', borderRadius: '4px', p: 1, mt: -1 }}>
                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#92400E', fontFamily: poppins, mb: 0.5 }}>
                                    {'⚠'} Similar existing FAQs — check before adding:
                                </Typography>
                                {sim.map(({ item, sim: s }) => (
                                    <Typography key={item.faqId} sx={{ fontSize: '0.7rem', color: '#78350F', fontFamily: poppins, pl: 1 }}>
                                        {'·'} {item.title} <span style={{ color: '#A8A29E' }}>(FAQ #{item.faqId} · {Math.round(s * 100)}% match)</span>
                                    </Typography>
                                ))}
                            </Box>
                        );
                    })()}
                    <TextField label="Description" size="small" multiline rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: poppins } }} />
                    {/* Image Upload */}
                    <Box>
                        <Typography sx={{ fontFamily: poppins, fontSize: '0.8rem', fontWeight: 500, color: '#374151', mb: 0.5 }}>Images</Typography>
                        <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} size="small"
                            sx={{ fontFamily: poppins, fontSize: '0.78rem', textTransform: 'none', borderRadius: '8px', mb: 1 }}>
                            Upload Image
                            <input type="file" hidden accept="image/*" multiple onChange={async (e) => {
                                for (const file of e.target.files) {
                                    try {
                                        const res = await uploadFaqImage(file);
                                        const newUrl = res.data.url;
                                        setForm(prev => {
                                            const existing = prev.imageUrls ? prev.imageUrls.split('\n').filter(Boolean) : [];
                                            return { ...prev, imageUrls: [...existing, newUrl].join('\n') };
                                        });
                                    } catch (err) { alert('Upload failed: ' + (err.response?.data || err.message)); }
                                }
                                e.target.value = '';
                            }} />
                        </Button>
                        {form.imageUrls && form.imageUrls.split('\n').filter(Boolean).map((url, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <img src={url.startsWith('/') ? `${window.location.protocol}//${window.location.hostname}:5100${url}` : url} alt="" style={{ width: 48, height: 48, borderRadius: 6, objectFit: 'cover', border: '1px solid #E2E8F0' }} />
                                <Typography sx={{ fontFamily: poppins, fontSize: '0.72rem', color: '#64748B', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{url}</Typography>
                                <IconButton size="small" onClick={() => {
                                    const urls = form.imageUrls.split('\n').filter(Boolean);
                                    urls.splice(i, 1);
                                    setForm({ ...form, imageUrls: urls.join('\n') });
                                }}><CloseIcon sx={{ fontSize: 14 }} /></IconButton>
                            </Box>
                        ))}
                    </Box>
                    <TextField label="YouTube Links (one per line)" size="small" multiline rows={2} value={form.videoLinks} onChange={(e) => setForm({ ...form, videoLinks: e.target.value })}
                        helperText="Enter YouTube URLs, one per line"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: poppins } }} />
                    <TextField label="Sort Order" size="small" type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: parseInt(e.target.value) || 0 })}
                        sx={{ width: 120, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: poppins } }} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setOpen(false)} sx={{ fontFamily: poppins, textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={!form.title.trim() || !form.category.trim()}
                        sx={{ fontFamily: poppins, textTransform: 'none', borderRadius: '8px', background: 'linear-gradient(135deg,#1A8CFF,#0F62CC)', boxShadow: 'none' }}>
                        {form.faqId ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs">
                <DialogTitle sx={{ fontFamily: poppins, fontWeight: 600 }}>Delete FAQ?</DialogTitle>
                <DialogContent><Typography sx={{ fontFamily: poppins, fontSize: '0.85rem' }}>This FAQ will be removed. This action cannot be undone.</Typography></DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteId(null)} sx={{ fontFamily: poppins, textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDelete} sx={{ fontFamily: poppins, textTransform: 'none', borderRadius: '8px', boxShadow: 'none' }}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
